import { getDefaultClient } from '../../../client/defaultClient';
import { getWalletProviderFromWalletAccount } from '../utils/getWalletProviderFromWalletAccount';
import type { WalletAccount } from '../walletAccount';
import { WalletAccountNotSelectedError } from './../../../errors/WalletAccountNotSelectedError';

type AssertWalletAccountSigningAvailabilityParams = {
  walletAccount: WalletAccount;
};

/**
 * Asserts that a wallet account is available and ready for signing operations.
 *
 * This function verifies that the specified wallet account is currently
 * connected and selected in the wallet app, ensuring it can perform signing operations.
 *
 * If the wallet account is not available, the user might need to reconnect it or
 * change the account in their wallet app to make it active.
 *
 * @param params.walletAccount - The wallet account to verify signing availability for.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves if the wallet is available for signing.
 * @throws WalletAccountNotSelectedError If the wallet account is not currently selected.
 */
export const assertWalletAccountSigningAvailability = async (
  { walletAccount }: AssertWalletAccountSigningAvailabilityParams,
  client = getDefaultClient()
) => {
  const walletProvider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  const { addresses } = await walletProvider.getConnectedAddresses();

  if (addresses.length > 0 && !addresses.includes(walletAccount.address)) {
    throw new WalletAccountNotSelectedError({
      expectedAddress: walletAccount.address,
      selectedAddress: addresses[0],
    });
  }
};
